// src/components/WorkflowMessage.tsx
// Vercel-inspired workflow message renderer with sophisticated design

import React from 'react';

interface WorkflowStep {
  number: number;
  description: string;
  status: 'complete' | 'in_progress' | 'pending';
  duration?: string;
}

interface FileDownload {
  filename: string;
  url: string;
  size?: string;
  count?: number;
  row_count?: number;
}

interface WorkflowMessageProps {
  content: string;
  generatedFiles?: FileDownload[];
}

const WorkflowMessage: React.FC<WorkflowMessageProps> = ({ content, generatedFiles }) => {
  // Strip any base64 image data that leaked into content (charts are rendered separately)
  const cleanContent = content
    .replace(/<!--CHART:[\s\S]*?-->/g, '')
    .replace(/!\[.*?\]\(data:image\/[^)]+\)/g, '')
    .replace(/\(data:image\/[^\s)]+\)/g, '')
    .replace(/data:image\/png;base64,[A-Za-z0-9+/=]{100,}/g, '[chart rendered below]')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  // Parse markdown (simple version for workflow messages)
  const parseMarkdown = (text: string): string => {
    // Convert **bold** to <strong>
    text = text.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold">$1</strong>');

    // Convert headers ## to <h2>, ### to <h3>
    text = text.replace(/^### (.+)$/gm, '<h3 class="text-base font-semibold mt-4 mb-2">$1</h3>');
    text = text.replace(/^## (.+)$/gm, '<h2 class="text-lg font-bold mt-4 mb-2">$1</h2>');

    // Convert line breaks to <br>
    text = text.replace(/\n/g, '<br />');

    // Convert bullets
    text = text.replace(/^- (.+)$/gm, '<li class="ml-4">• $1</li>');

    return text;
  };

  // Transform backend status messages to Manus-style format
  const transformWorkflowMessage = (message: string): string => {
    // Searching patterns
    if (message.match(/searching the web for:?/i)) {
      const query = message.replace(/searching the web for:?\s*/i, '').replace(/\.\.\.$/, '');
      return `Searching ${query}`;
    }

    // Knowledge base searching
    if (message.match(/searching knowledge base/i)) {
      return 'Searching knowledge base';
    }

    // Code execution
    if (message.match(/executing .+ code/i)) {
      const language = message.match(/executing (.+?) code/i)?.[1] || 'code';
      return `Executing ${language} code`;
    }

    // Browsing (can be added when backend sends these messages)
    if (message.match(/browsing\s+https?:\/\//i)) {
      const url = message.replace(/browsing\s*/i, '').replace(/\.\.\.$/, '');
      // Extract domain for cleaner display
      try {
        const urlObj = new URL(url);
        return `Browsing ${urlObj.hostname}${urlObj.pathname.substring(0, 30)}${urlObj.pathname.length > 30 ? '...' : ''}`;
      } catch {
        return `Browsing ${url.substring(0, 50)}${url.length > 50 ? '...' : ''}`;
      }
    }

    // Scraping patterns
    if (message.match(/scraping/i)) {
      let scrapingText = message.replace(/\.\.\.$/, '');
      const urlMatch = scrapingText.match(/https?:\/\/([^\/\s]+)/);
      if (urlMatch) {
        scrapingText = `Scraping ${urlMatch[1]}`;
      }
      return scrapingText;
    }

    // Planning patterns
    if (message.match(/planning .+ structure/i) || message.match(/preparing/i)) {
      return message;
    }

    // Generating patterns
    if (message.match(/generating/i) || message.match(/stitching/i)) {
      return message;
    }

    // Analyzing patterns
    if (message.match(/analyzing/i)) {
      return message;
    }

    // Found results
    if (message.match(/found \d+ (sources|documents|results|products)/i)) {
      return message;
    }

    // No results patterns
    if (message.match(/no (results|documents) found/i)) {
      return message;
    }

    // Presentation/Success ready patterns
    if (message.match(/ready|completed successfully/i)) {
      return message;
    }

    // Default - return as-is
    return message;
  };

  // Parse workflow steps from content
  const parseWorkflowSteps = (): WorkflowStep[] => {
    const steps: WorkflowStep[] = [];
    const lines = cleanContent.split('\n');

    for (const line of lines) {
      // Match patterns like: "▶️ Step 1/3: Scraping..." or "✅ Step 1/3 completed"
      const stepMatch = line.match(/[▶️✅❌⏳]\s*Step\s+(\d+)\/(\d+):\s*(.+)/i);
      if (stepMatch) {
        const stepNum = parseInt(stepMatch[1]);
        let description = stepMatch[3].trim();

        // Transform the description using our message transformer
        description = transformWorkflowMessage(description);

        const status = line.includes('✅') || line.toLowerCase().includes('complete')
          ? 'complete'
          : line.includes('▶️') || line.toLowerCase().includes('in progress')
          ? 'in_progress'
          : 'pending';

        steps.push({
          number: stepNum,
          description,
          status
        });
      }
    }

    return steps;
  };

  // Extract summary/results section
  const extractResults = (): string => {
    const resultsMatch = cleanContent.match(/\*\*Results:\*\*([\s\S]*?)(?:\n\n|$)/i);
    if (resultsMatch) {
      return resultsMatch[1].trim();
    }

    // Look for summary after the last step
    const lines = cleanContent.split('\n');
    // Find last step index by iterating from end
    let lastStepIndex = -1;
    for (let i = lines.length - 1; i >= 0; i--) {
      if (lines[i].match(/Step\s+\d+/i)) {
        lastStepIndex = i;
        break;
      }
    }

    if (lastStepIndex >= 0 && lastStepIndex < lines.length - 1) {
      return lines.slice(lastStepIndex + 1).join('\n').trim();
    }

    return '';
  };

  const steps = parseWorkflowSteps();
  const results = extractResults();
  const hasSteps = steps.length > 0;

  return (
    <div className="space-y-4">
      {/* Workflow Title */}
      {hasSteps && (
        <div className="flex items-center space-x-2 pb-3 border-b border-border/30">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500/20 to-blue-500/20">
            <span className="text-lg">🎯</span>
          </div>
          <h3 className="text-base font-semibold text-foreground">
            Workflow Execution
          </h3>
        </div>
      )}

      {/* Timeline Progress (Vercel-style) */}
      {hasSteps && (
        <div className="space-y-3">
          {steps.map((step, index) => (
            <div key={index} className="flex items-start space-x-3 group">
              {/* Step indicator */}
              <div className="relative flex flex-col items-center">
                {/* Circle */}
                <div className={`flex items-center justify-center w-7 h-7 rounded-full border-2 transition-all duration-300 ${
                  step.status === 'complete'
                    ? 'bg-green-500/20 border-green-500 text-green-600'
                    : step.status === 'in_progress'
                    ? 'bg-blue-500/20 border-blue-500 text-blue-600 animate-pulse'
                    : 'bg-muted border-border text-muted-foreground'
                }`}>
                  {step.status === 'complete' ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : step.status === 'in_progress' ? (
                    <div className="w-2 h-2 rounded-full bg-blue-500" />
                  ) : (
                    <span className="text-xs font-medium">{step.number}</span>
                  )}
                </div>

                {/* Connecting line */}
                {index < steps.length - 1 && (
                  <div className={`w-0.5 h-8 mt-1 transition-colors duration-300 ${
                    step.status === 'complete' ? 'bg-green-500/30' : 'bg-border'
                  }`} />
                )}
              </div>

              {/* Step content */}
              <div className="flex-1 pt-1 pb-2">
                <div className="flex items-center justify-between">
                  <p className={`text-sm font-medium transition-colors ${
                    step.status === 'complete'
                      ? 'text-foreground'
                      : step.status === 'in_progress'
                      ? 'text-foreground'
                      : 'text-muted-foreground'
                  }`}>
                    {step.description}
                  </p>

                  {step.status === 'complete' && step.duration && (
                    <span className="text-xs text-muted-foreground">
                      {step.duration}
                    </span>
                  )}
                </div>

                {step.status === 'in_progress' && (
                  <div className="mt-2 h-1 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500 animate-progress-bar rounded-full" />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Main Content - Show when there are NO steps (LLM workflow fallback) */}
      {!hasSteps && generatedFiles && generatedFiles.length > 0 && (
        <div
          className="text-sm text-foreground/90 leading-relaxed mb-4"
          dangerouslySetInnerHTML={{ __html: parseMarkdown(cleanContent) }}
        />
      )}

      {/* Results Section */}
      {results && !generatedFiles && (
        <div className="mt-4 p-4 rounded-lg bg-muted/30 border border-border/50">
          <div className="flex items-center space-x-2 mb-2">
            <span className="text-sm font-semibold text-foreground">Results</span>
          </div>
          <div
            className="text-sm text-foreground/80 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: parseMarkdown(results) }}
          />
        </div>
      )}

      {/* Compact File Download Bar — matches workflow project style */}
      {generatedFiles && generatedFiles.length > 0 && (
        <div className="mt-4 space-y-2">
          {generatedFiles.map((file, index) => (
            <div
              key={index}
              className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-card/80"
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 text-primary flex-shrink-0">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">{file.filename}</p>
                {(file.count || file.row_count) && (
                  <p className="text-xs text-muted-foreground">{file.count || file.row_count} rows</p>
                )}
              </div>
              <a
                href={file.url}
                download={file.filename}
                onClick={(e) => { e.preventDefault(); window.open(file.url, '_blank'); }}
                className="flex-shrink-0 px-4 py-2 text-sm font-semibold text-white rounded-lg"
                style={{ background: 'linear-gradient(135deg, #00b894, #00cec9)' }}
              >
                Download
              </a>
            </div>
          ))}
        </div>
      )}

      {/* Fallback: Show content with markdown parsing if no steps detected */}
      {!hasSteps && !generatedFiles && (
        <div
          className="text-sm text-foreground/90 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: parseMarkdown(cleanContent) }}
        />
      )}
    </div>
  );
};

export default WorkflowMessage;
